import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  BellIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const TYPE_CONFIG = {
  work_order_assigned: { color: 'bg-blue-100 text-blue-700', label: 'Assignment' },
  work_order_status_change: { color: 'bg-yellow-100 text-yellow-700', label: 'Status Change' },
  work_order_completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },
  work_order_note: { color: 'bg-purple-100 text-purple-700', label: 'Note' },
  general: { color: 'bg-gray-100 text-gray-700', label: 'General' }
};

const Notifications = () => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [page, showUnreadOnly]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, unreadOnly: showUnreadOnly });
      const response = await fetch(`/api/notifications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      }
    } catch (error) { console.error('Error:', error); }
    finally { setIsLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n));
    } catch (error) { console.error('Error:', error); }
  };

  const markAllAsRead = async () => {
    try {
      await fetch('/api/notifications/read-all', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
    } catch (error) { console.error('Error:', error); }
  };

  const handleClick = (notification) => {
    if (!notification.isRead) {
      // Optimistically mark as read in UI immediately
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n));
      // Fire API call without awaiting
      fetch(`/api/notifications/${notification.id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => console.error('Error marking read:', err));
    }
    if (notification.referenceType === 'work_order' && notification.referenceId) {
      navigate(`/work-orders?id=${notification.referenceId}`);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BellIcon className="h-6 w-6" />
            Notifications
          </h1>
          <p className="text-gray-600 text-sm">{total} total notifications</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showUnreadOnly}
              onChange={e => { setShowUnreadOnly(e.target.checked); setPage(1); }}
              className="h-4 w-4 text-brand-green-600 rounded"
            />
            Unread only
          </label>
          <button
            onClick={markAllAsRead}
            className="text-sm text-brand-green-600 hover:text-brand-green-700"
          >
            Mark all read
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow divide-y">
        {isLoading && notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BellIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p>No notifications</p>
          </div>
        ) : (
          notifications.map(n => {
            const typeConfig = TYPE_CONFIG[n.type] || TYPE_CONFIG.general;
            return (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`p-4 cursor-pointer hover:bg-gray-50 flex items-start gap-3 ${!n.isRead ? 'bg-blue-50/50' : ''}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {!n.isRead ? (
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-brand-green-500" />
                  ) : (
                    <CheckIcon className="h-4 w-4 text-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm text-gray-900">{n.title}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                  </div>
                  {n.message && <p className="text-sm text-gray-600 truncate">{n.message}</p>}
                  <span className="text-xs text-gray-400">{formatTime(n.createdAt)}</span>
                </div>
                {!n.isRead && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-brand-green-600"
                    title="Mark as read"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-md border disabled:opacity-50 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-md border disabled:opacity-50 hover:bg-gray-50"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Notifications;
